import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import s from './NewsFeed.module.css';

const NewsFeed = () => {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(0);
  const [initialLoading, setInitialLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const navigate = useNavigate();

  // Дебаунс поиска - исправлен на 3 символа
  useEffect(() => {
    const handler = setTimeout(() => {
      const trimmed = searchInput.trim();
      if (trimmed.length >= 3 || trimmed === '') {
        setSearchQuery(trimmed);
        setPage(0);
        setNews([]);
        setHasMore(true);
      }
    }, 500);

    return () => clearTimeout(handler);
  }, [searchInput]);

  // Загрузка данных при изменении поиска или страницы
  useEffect(() => {
    if (page === 0 && news.length === 0) {
      loadInitialNews();
    } else if (page > 0) {
      loadMoreNews();
    }
  }, [page, searchQuery]);

  const loadInitialNews = async () => {
    setInitialLoading(true);
    try {
      const params = new URLSearchParams({
        page: '0',
        size: '5'
      });

      if (searchQuery.length >= 3) {
        params.append('query', searchQuery);
      }

      // Используем публичный эндпоинт (без /admin)
      const url = searchQuery.length >= 3
          ? `/api/articles/search?${params.toString()}`
          : `/api/articles/feed?${params.toString()}`;

      const response = await fetch(url);
      if (response.ok) {
        const data = await response.json();
        setNews(data.content || []);
        setHasMore(!data.last);
        setPage(1);
      }
    } catch (error) { console.error('Ошибка загрузки статей:', error); }
    finally { setInitialLoading(false); }
  };

  const loadMoreNews = async () => {
    if (loading || !hasMore) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        size: '5'
      });

      if (searchQuery.length >= 3) {
        params.append('query', searchQuery);
      }

      const url = searchQuery.length >= 3
          ? `/api/articles/search?${params.toString()}`
          : `/api/articles/feed?${params.toString()}`;

      const response = await fetch(url);
      if (response.ok) {
        const data = await response.json();
        const pageMeta = data.page || {};
        const totalPages = (typeof pageMeta.totalPages !== 'undefined') ? pageMeta.totalPages : data.totalPages;
        const number = (typeof pageMeta.number !== 'undefined') ? pageMeta.number : (typeof data.number !== 'undefined' ? data.number : page);
        const content = Array.isArray(data.content) ? data.content : [];
        const computedHasMore = (typeof totalPages === 'number') ? (number < totalPages - 1) : (content.length > 0);

        if (content.length > 0) {
          setNews(prev => [...prev, ...content]);
        }
        setHasMore(computedHasMore);
        if (computedHasMore) {
          setPage(prev => prev + 1);
        }
      } else if (response.status === 401) {
        setHasMore(false);
      }
    } catch (error) {
      console.error('Ошибка загрузки дополнительных статей:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (e) => {
    setSearchInput(e.target.value);
  };

  const handleClearSearch = () => {
    setSearchInput('');
    setSearchQuery('');
    setPage(0);
    setNews([]);
    setHasMore(true);
  };

  if (initialLoading) return (
      <div className={s.container}>
        <h2 className={s.title}>Лента статей</h2>

        <div className={s.filtersSection}>
          <div className={s.searchBox}>
            <input
                type="text"
                placeholder="Поиск по названию (минимум 3 символа)..."
                value={searchInput}
                onChange={handleSearchChange}
                className={s.searchInput}
            />
            {searchInput && (
                <button
                    className={s.clearSearch}
                    onClick={handleClearSearch}
                    type="button"
                >
                  ×
                </button>
            )}
          </div>
        </div>

        <div className={s.skeletonGrid}>
          {[...Array(5)].map((_, i) => (
              <div key={i} className={s.skeletonCard}>
                <div className={s.skeletonImage}></div>
                <div className={s.skeletonContent}>
                  <div className={s.skeletonTitle}></div>
                  <div className={s.skeletonText}></div>
                  <div className={s.skeletonText}></div>
                  <div className={s.skeletonMeta}></div>
                </div>
              </div>
          ))}
        </div>
      </div>
  );

  const showEmptyState = news.length === 0 && !initialLoading;

  return (
      <div className={s.container}>
        <h2 className={s.title}>Лента статей</h2>

        {/* Только поиск, без фильтров по статусу */}
        <div className={s.filtersSection}>
          <div className={s.searchBox}>
            <input
                type="text"
                placeholder="Поиск по названию (минимум 3 символа)..."
                value={searchInput}
                onChange={handleSearchChange}
                className={s.searchInput}
            />
            {searchInput && (
                <button
                    className={s.clearSearch}
                    onClick={handleClearSearch}
                    type="button"
                >
                  ×
                </button>
            )}
          </div>
        </div>

        {showEmptyState ? (
            <div className={s.emptyState}>
              <div className={s.emptyIcon}>
                <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M4 19.5V4.5C4 3.397 4.897 2.5 6 2.5H18C19.103 2.5 20 3.397 20 4.5V19.5C20 20.604 19.103 21.5 18 21.5H6C4.897 21.5 4 20.604 4 19.5Z" stroke="currentColor" strokeWidth="2"/>
                  <path d="M8 7.5H16" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  <path d="M8 11.5H12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  <path d="M8 15.5H14" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </div>
              <h3>
                {searchQuery.length >= 3
                    ? `По запросу "${searchQuery}" ничего не найдено`
                    : 'Статьи не найдены'
                }
              </h3>
              <p>
                {searchQuery.length >= 3
                    ? 'Попробуйте изменить поисковый запрос'
                    : 'Когда статьи появятся, они будут отображаться здесь'
                }
              </p>
            </div>
        ) : (
            <>
              <div className={s.newsGrid}>
                {news.map((item) => (
                    <article key={item.id} className={s.newsCard}>
                      <div className={s.newsImage}>
                        {item.coverImageUrl ? (
                            <img
                                src={item.coverImageUrl}
                                alt={item.name}
                                loading="lazy"
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                  e.target.nextSibling.style.display = 'flex';
                                }}
                            />
                        ) : null}
                        <div className={s.imagePlaceholder} style={{ display: item.coverImageUrl ? 'none' : 'flex' }}>
                          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M4 5H20V16L15.5 12L10 16L7 13L4 16V5Z" fill="currentColor"/>
                            <circle cx="15" cy="8" r="2" fill="white"/>
                          </svg>
                        </div>
                        {item.isActual && (<span className={s.actualBadge}>Актуально</span>)}
                      </div>

                      <div className={s.newsContent}>
                        <h3>{item.name}</h3>
                        <p>{item.description}</p>

                        <div className={s.newsMeta}>
                    <span className={s.publishDate}>
                      {item.publishedAt ? new Date(item.publishedAt).toLocaleDateString('ru-RU') : 'Не опубликовано'}
                    </span>
                        </div>

                        <div className={s.actions}>
                          <button className={s.readButton} onClick={() => navigate(`/articles/${item.id}`)}>
                            Читать
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M5 12H19M19 12L12 5M19 12L12 19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          </button>
                          {item.source && (
                              <a href={item.source} target="_blank" rel="noopener noreferrer" className={s.sourceLink}>
                                Источник
                              </a>
                          )}
                        </div>
                      </div>
                    </article>
                ))}
              </div>

              {hasMore && (
                  <div className={s.loadMoreContainer}>
                    <button className={s.loadMoreButton} onClick={() => setPage(prev => prev + 1)} disabled={loading}>
                      {loading ? (
                          <>
                            <span className={s.spinner}></span>
                            Загрузка...
                          </>
                      ) : 'Показать ещё'}
                    </button>
                  </div>
              )}
            </>
        )}
      </div>
  );
};

export default NewsFeed;